import { useCallback } from 'react';
import YouboraTracking from 'providers/player/YouboraTracking';
import { ContentLinkType } from 'types/Tracking';

export interface SearchClickParams {
    section: string;
    sectionOrder: number;
    column: number;
    row: number;
    contentId: string;
    query: string;
    contentLink?: string;
    contentLinkType?: ContentLinkType;
}

export interface UseYouboraSearchTrackingReturn {
    trackSearchQuery: (query: string) => void;
    trackSearchResult: (resultCount: number, query: string) => void;
    trackSearchClick: (params: SearchClickParams) => void;
}

export const useYouboraSearchTracking = (): UseYouboraSearchTrackingReturn => {
    const trackSearchQuery = useCallback((query: string) => {
        const youboraTracking = YouboraTracking.getInstance();

        if (!youboraTracking.isInitialized || !query) {
            return;
        }

        try {
            youboraTracking.trackSearchQuery(query);
        } catch (error) {
            console.error('[Youbora] Error tracking search query:', error);
        }
    }, []);

    const trackSearchResult = useCallback((resultCount: number, query: string) => {
        const youboraTracking = YouboraTracking.getInstance();

        if (!youboraTracking.isInitialized || !query) {
            return;
        }

        try {
            youboraTracking.trackSearchResult(resultCount, query);
        } catch (error) {
            console.error('[Youbora] Error tracking search result:', error);
        }
    }, []);

    const trackSearchClick = useCallback((params: SearchClickParams) => {
        const youboraTracking = YouboraTracking.getInstance();

        if (!youboraTracking.isInitialized || !params.query || !params.contentId) {
            return;
        }

        try {
            const customParams =
                params.contentLink && params.contentLinkType
                    ? { contentLink: params.contentLink, contentLinkType: params.contentLinkType }
                    : undefined;
            youboraTracking.trackSearchClick(
                params.section,
                params.sectionOrder,
                params.column,
                params.row,
                params.contentId,
                params.query,
                customParams
            );
        } catch (error) {
            console.error('[Youbora] Error tracking search click:', error);
        }
    }, []);

    return {
        trackSearchQuery,
        trackSearchResult,
        trackSearchClick,
    };
};

export default useYouboraSearchTracking;
